import React, { useRef, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useForm, FieldValues } from 'react-hook-form';
import { FiPaperclip } from 'react-icons/fi';
import getTime from '@entities/issue/lib/get-time';
import getFileSize from '@entities/issue/lib/get-file-size';
import compare from '@entities/issue/lib/comparer';
import hexConverter from '@entities/issue/lib/hex-converter';
import { LoadingWrapper } from '../../shared/ui/loading-wrapper';
import { useAppDispatch, useAppSelector } from '../../app/store/store';
import {
  getIssueAttachment,
  getIssueById,
  postCommentOnIssue,
  resetCurrentIssue,
} from '../../entities/issue/model';
import { renderTextWithLinks } from '@shared/lib/render-text-with-links';
import { IssueAttachement, IssueComment } from '../../types/issueInterfaces';
import { Nullable } from '../../types/interfaces';
import { parseHtmlTable } from '@entities/issue/lib/parse-html-table';
import { openLinkInNewWindow } from '@shared/lib/open-link-new-window';
import { selectUser } from '../../entities/auth/model';

const STATUS_TRANSLATE: Record<string, string> = {
  'Work in progress': 'В работе',
  Postponed: 'Отложено',
  Resolved: 'Решено',
  Closed: 'Закрыто',
};

// Инициалы из имени okdesk-юзера. Имя приходит в одном поле, поэтому
// разрезаем по пробелу и берём первые буквы первых двух частей.
const getInitials = (name?: string): string => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  const first = parts[0]?.charAt(0).toUpperCase() ?? '';
  const second = parts[1]?.charAt(0).toUpperCase() ?? '';
  return (first + second) || '?';
};

// Маленькая инфо-пара label → value: ровно тот же визуал, что в
// HelpdeskTicket — серый полужирный label сверху, тёмное значение снизу.
// Используется во всех инфо-блоках левой колонки.
const InfoRow: React.FC<{
  label: string;
  children: React.ReactNode;
}> = ({ label, children }) => (
  <div className="flex flex-col gap-[5px]">
    <p className="text-[#bcbcbc] font-semibold text-sm">{label}</p>
    <div className="font-medium text-sm text-[#242426] flex items-center gap-[10px]">
      {children}
    </div>
  </div>
);

export default function Issue() {
  const dispatch = useAppDispatch();
  const { id: issueId } = useParams();

  const { issueDetails: issue, comments } =
    useAppSelector((state) => state.issues.currentIssue) ?? {};
  const status = useAppSelector((state) => state.issues.status);
  const me = useAppSelector(selectUser);
 
  const ISSUE_INTERNAL_GROUPS = [
    'MANAGER',
    'OFFICE_MANAGER',
    'SALES_DIRECTOR',
    'HD_HEAD',
    'HD_ENGINEER',
    'HD_SERVICE_ENGINEER',
    'admins',
  ];
  const ISSUE_CLIENT_GROUPS = ['COMPANY_OWNER', 'USER', 'BASIC'];
  const issueUserGroups = me?.groups ?? [];
  const hasIssueInternalGroup = issueUserGroups.some((g: string) =>
    ISSUE_INTERNAL_GROUPS.includes(g)
  );
  const hasIssueClientGroup = issueUserGroups.some((g: string) =>
    ISSUE_CLIENT_GROUPS.includes(g)
  );
  const isStaff =
    hasIssueInternalGroup ||
    (!hasIssueClientGroup &&
      Boolean(me?.is_staff || me?.is_superuser));

  const scrollRef = useRef<Nullable<HTMLDivElement>>(null);
  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm<{ content: string; attachments?: FileList }>({
    defaultValues: { content: '' },
  });

  // Подгружаем тикет при заходе и сбрасываем при размонтировании,
  // иначе при открытии следующего обращения первый кадр покажет старые
  // данные.
  useEffect(() => {
    if (issueId) dispatch(getIssueById(Number(issueId)));
    return () => {
      dispatch(resetCurrentIssue());
    };
  }, [dispatch, issueId]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [comments]);

  const onSubmit = (data: FieldValues) => {
    if (!data.content?.trim()) return;
    dispatch(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      postCommentOnIssue({
        id: Number(issueId),
        author_id: issue?.contact?.id || 1,
        ...data,
      } as any)
    );
    reset();
  };

  const regExp = /<[^>]*>/g;
  const devicesOnDiagnostics = parseHtmlTable(issue?.description);
  const hasDevices =
    devicesOnDiagnostics.length > 0 &&
    devicesOnDiagnostics.some((d) => d.serialNumber || d.model);

  // Группируем сообщения по дням, чтобы рисовать аккуратные разделители
  // вида «23 Апреля». Логика та же, что была в исходной Issue.
  let firstDay = getTime(issue?.created_at, false, false, true);
  const isAnotherDay = (published: string) => {
    const publishedDay = getTime(published, false, false, true);
    if (publishedDay > firstDay) {
      firstDay = publishedDay;
      return true;
    }
    return false;
  };

  const statusName = issue?.status?.name;
  const statusLabel = statusName
    ? STATUS_TRANSLATE[statusName] || statusName
    : '—';
  // Цвет окдеск отдаёт hex'ом — тот же hexConverter, что и в списке,
  // делает мягкий фон для бейджа.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const statusColor = (issue?.status as any)?.color || '#6c6c6c';

  return (
    <div
      className="grid gap-x-[30px] gap-y-5"
      style={{
        gridTemplateColumns: '2fr 2.5fr',
        gridTemplateRows: '30px 1fr',
        gridTemplateAreas: '"back back" "info chat"',
      }}
    >
      <LoadingWrapper status={status}>
        {/* ── Back link ── */}
        <Link
          className="[grid-area:back] text-left font-semibold text-sm text-[#b4b4b4] flex items-center gap-[5px] before:content-[''] before:inline-block before:border-b-2 before:border-l-2 before:border-[#b4b4b4] before:w-2 before:h-2 before:rotate-45 before:mr-[5px] before:align-middle"
          to="/devices"
        >
          Обращения на диагностику
        </Link>

        {/* ── Info panel: оборудование, даты, статус, ответственный ── */}
        <div className="[grid-area:info] p-[27px_31px] flex flex-col gap-[20px] w-full bg-white rounded-2xl shadow-[0_2px_12px_rgba(0,0,0,0.08)] overflow-y-auto">
          {/* Шапка карточки тикета. Раньше тут был один заголовок
              «Оборудование на диагностике от {date}» и больше ничего —
              из-за чего staff на странице тикета не видел номер
              заявки и название компании. Теперь:
                * номер заявки идёт первой строкой (ссылка-чип-шрифт),
                  чтобы было удобно сослаться в чате/письме;
                * имя компании показываем только staff'у (для клиента
                  это его собственная компания, ему не нужно).
          */}
          {issue?.id ? (
            <p className="text-[#bcbcbc] font-mono text-xs">
              Заявка #{issue.id}
            </p>
          ) : null}
          <p className="text-[#242426] font-bold text-base">
            {`Оборудование на диагностике от ${
              issue?.created_at ? getTime(issue.created_at, false, false) : '—'
            }`}
          </p>
          {isStaff && (issue as any)?.company?.name ? (
            <InfoRow label="Компания">
              <span>{(issue as any).company.name}</span>
            </InfoRow>
          ) : null}

          <div className="flex flex-col gap-[20px]">
            {/* Список оборудования — теперь оформлен как структурный
                перечень с моделью, серийником и плашкой гарантии,
                а не свалкой строчек. */}
            <div className="flex flex-col gap-[5px]">
              <p className="text-[#bcbcbc] font-semibold text-sm">
                Список оборудования
              </p>
              {hasDevices ? (
                <ul className="flex flex-col gap-1.5 mt-1">
                  {devicesOnDiagnostics.map((d) => (
                    <li
                      key={`${d.model}-${d.serialNumber}`}
                      className="flex items-center justify-between gap-3 py-1.5 border-b border-[#efefef] last:border-b-0"
                    >
                      <div className="min-w-0">
                        <p className="text-[#242426] text-sm font-medium truncate">
                          {d.model || '—'}
                        </p>
                        {d.serialNumber ? (
                          <p className="text-[#bcbcbc] text-xs font-mono">
                            №{d.serialNumber}
                          </p>
                        ) : null}
                      </div>
                      {d.isWarrantyExpired ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-[#FDEAEA] text-[#B42318] whitespace-nowrap">
                          гарантия истекла
                        </span>
                      ) : null}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-[#bcbcbc]">—</p>
              )}
            </div>

            <InfoRow label="Дата регистрации обращения">
              {issue?.created_at ? getTime(issue.created_at) : '—'}
            </InfoRow>

            <InfoRow label="Планируемая дата решения обращения">
              {issue?.planned_reaction_at
                ? getTime(issue.planned_reaction_at)
                : '—'}
            </InfoRow>

            <InfoRow label="Статус">
              <span
                className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold"
                style={{
                  color: statusColor,
                  backgroundColor: hexConverter(statusColor),
                }}
              >
                {statusLabel}
              </span>
            </InfoRow>

            <InfoRow label="Ответственный сотрудник">
              {issue?.assignee?.name ? (
                <>
                  <div className="w-8 h-8 rounded-full bg-[#3B82F6] flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0">
                    {getInitials(issue.assignee.name)}
                  </div>
                  <span>{issue.assignee.name}</span>
                </>
              ) : (
                <span className="text-[#bcbcbc]">Не назначен</span>
              )}
            </InfoRow>

            {issue?.contact?.name ? (
              <InfoRow label="Контакт">
                <div className="w-8 h-8 rounded-full bg-[#43A047] flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0">
                  {getInitials(issue.contact.name)}
                </div>
                <span>{issue.contact.name}</span>
              </InfoRow>
            ) : null}
          </div>
        </div>

        {/* ── Chat panel — визуально клон HelpdeskTicket ── */}
        <div className="[grid-area:chat] flex flex-col w-full bg-white rounded-2xl shadow-[0_2px_12px_rgba(0,0,0,0.08)] min-h-[650px]">
          <p className="text-[#242426] font-bold text-base px-[27px] pt-[27px] pb-3">
            Комментарии ({comments?.length || 0})
          </p>

          <div
            ref={scrollRef}
            className="flex-1 px-[25px] py-[15px] flex flex-col gap-5 overflow-y-auto max-h-[500px] scroll-smooth [scrollbar-color:#e3282a_#fff] [scrollbar-width:thin]"
          >
            {comments && comments.length > 0 ? (
              comments
                .slice()
                .sort(compare)
                .map((comment: IssueComment) => {
                  const fromContact = comment.author?.type === 'contact';
                  const initials = getInitials(comment.author?.name);
                  const dayLabel = isAnotherDay(comment.published_at)
                    ? getTime(comment.published_at, false, false, true)
                    : '';
                  const time =
                    getTime(comment.published_at, true, false)?.split(
                      ', '
                    )[1] || '';

                  return (
                    <React.Fragment key={comment.id}>
                      {dayLabel ? (
                        <p className="text-[#bcbcbc] font-medium text-xs text-center">
                          {dayLabel}
                        </p>
                      ) : null}
                      <div
                        className={`relative max-w-[400px] flex flex-col gap-[5px] pt-3 pr-8 pb-5 pl-[19px] ${
                          fromContact
                            ? 'self-end bg-[rgba(227,40,42,0.12)] rounded-[15px_15px_0_15px]'
                            : 'self-start bg-[#efefef] rounded-[15px_15px_15px_0]'
                        }`}
                      >
                        <div className="text-sm flex justify-between items-start text-[#e3282a] font-semibold gap-3">
                          <div className="flex items-center gap-2 min-w-0">
                            <div
                              className="w-6 h-6 rounded-full flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0"
                              style={{
                                backgroundColor: fromContact
                                  ? '#E3282A'
                                  : '#3B82F6',
                              }}
                            >
                              {initials}
                            </div>
                            <span className="truncate">
                              {comment.author?.name || '—'}
                            </span>
                          </div>
                          <span className="font-medium text-[#bcbcbc] whitespace-nowrap text-xs">
                            {time}
                          </span>
                        </div>
                        <p className="text-[#242426] text-sm font-medium whitespace-pre-wrap break-words [&_a]:text-blue-600 [&_a:hover]:text-red-600 [&_a]:transition-colors">
                          {renderTextWithLinks(
                            comment.content.replace(regExp, '')
                          )}
                        </p>
                        {comment.attachments?.length ? (
                          <div className="flex flex-col gap-1 mt-1">
                            {comment.attachments.map(
                              (a: IssueAttachement) => (
                                <button
                                  type="button"
                                  key={a.id}
                                  onClick={() =>
                                    dispatch(
                                      getIssueAttachment({
                                        issueId: issueId ? +issueId : -1,
                                        attachmentId: a.id,
                                      })
                                    ).then((data) =>
                                      openLinkInNewWindow(
                                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                        (data.payload as any).attachment_url
                                      )
                                    )
                                  }
                                  className="flex items-center gap-2 text-left"
                                >
                                  <FiPaperclip className="w-3.5 h-3.5 text-[#bcbcbc] flex-shrink-0" />
                                  <span className="text-xs text-[#e3282a] truncate">
                                    {a.attachment_file_name}
                                  </span>
                                  <span className="text-xs text-[#bcbcbc] whitespace-nowrap">
                                    {getFileSize(a.attachment_file_size)}
                                  </span>
                                </button>
                              )
                            )}
                          </div>
                        ) : null}
                      </div>
                    </React.Fragment>
                  );
                })
            ) : (
              <p className="text-[#bcbcbc] text-sm text-center py-10">
                Сообщений пока нет
              </p>
            )}
          </div>

          {/* Форма ответа — те же ингредиенты, что в HelpdeskTicket:
              скрепка-FiPaperclip, текстовый инпут с тонкой рамкой и
              круглая красная кнопка отправки со стрелкой. */}
          <div className="border-t border-[#efefef]">
            <form
              onSubmit={handleSubmit(onSubmit)}
              className="px-[25px] pb-5 pt-3"
            >
              <div className="flex gap-[1.5%] items-center justify-between">
                <label className="cursor-pointer m-0 [&_input[type='file']]:hidden">
                  <input type="file" {...register('attachments')} />
                  <FiPaperclip className="w-[28px] h-[28px] text-[#bcbcbc] hover:text-[#e3282a] transition-colors cursor-pointer" />
                </label>
                <input
                  {...register('content', {
                    required: 'Невозможно отправить пустое сообщение',
                  })}
                  placeholder="Введите комментарий..."
                  className="py-[15px] px-[10px] border-[1.5px] border-[#dcdee0] flex-1 rounded"
                  autoComplete="off"
                />
                <button
                  type="submit"
                  className="w-10 h-10 flex items-center justify-center shrink-0 rounded-full bg-[#e3282a] hover:bg-[#C91F21] transition-all duration-300"
                >
                  <svg
                    className="w-5 h-5 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 12h14M12 5l7 7-7 7"
                    />
                  </svg>
                </button>
              </div>
              {errors.content && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.content?.message as string}
                </p>
              )}
              {watch('attachments') && watch('attachments')?.[0] && (
                <div className="flex gap-3 mt-2 text-xs text-[#bcbcbc] items-center">
                  <FiPaperclip className="w-3 h-3" />
                  <span>{watch('attachments')?.[0].name}</span>
                  <span>{getFileSize(watch('attachments')?.[0].size)}</span>
                </div>
              )}
            </form>
          </div>
        </div>
      </LoadingWrapper>
    </div>
  );
}
